import { useState } from "react";
import { PhoneCall } from "lucide-react";

import { api, errMsg } from "../../../lib/api.js";
import { logError } from "../../../lib/logger.js";
import { AsyncButton } from "../../ui/async-button.js";
import { Button } from "../../ui/button.js";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog.js";
import { Input } from "../../ui/input.js";
import { Label } from "../../ui/label.js";
import { SelectField } from "../../ui/select-field.js";
import { Textarea } from "../../ui/textarea.js";
import { ContactVerificationBadge } from "../../onboarding/ContactVerificationBadge.js";

export interface VerifiableContact {
  id: string; name: string; relationship: string; phone: string; isPrimary: boolean;
  verificationStatus: string;
  verificationCallDate: string | null;
  verificationNotes: string | null;
  verifiedBy: { employee: { firstName: string; lastName: string } | null } | null;
}

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
const todayISO = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD in local time

// HR records the verification call to each emergency contact: who called,
// when, and the outcome. A contact stays "Verification pending" until then,
// and activation needs two verified contacts (server gate 3).
export function EmergencyContactVerificationPanel({ contacts, canRecord, disabledReason, onChanged }: {
  contacts: VerifiableContact[];
  canRecord: boolean;
  disabledReason?: string | undefined;
  onChanged: () => Promise<unknown>;
}) {
  const [target, setTarget] = useState<VerifiableContact | null>(null);
  const [status, setStatus] = useState("VERIFIED");
  const [callDate, setCallDate] = useState(todayISO());
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  function open(c: VerifiableContact) {
    setTarget(c);
    setStatus(c.verificationStatus === "PENDING" ? "VERIFIED" : c.verificationStatus);
    setCallDate(todayISO());
    setNotes(c.verificationNotes ?? "");
    setError("");
  }

  async function save() {
    if (!target) return;
    setError("");
    if (status !== "VERIFIED" && !notes.trim()) {
      setError("Add a note saying what happened on the call.");
      throw new Error("validation");
    }
    try {
      await api.patch(`/emergency-contacts/${target.id}/verification`, { status, callDate, notes: notes.trim() || undefined });
      await onChanged();
      setTarget(null);
    } catch (e) {
      logError("EmergencyContactVerificationPanel.save", e, { contactId: target.id });
      setError(errMsg(e) ?? "Could not save the verification.");
      throw e;
    }
  }

  const verified = contacts.filter(c => c.verificationStatus === "VERIFIED").length;

  return (
    <div className="border-b border-surface-100 py-2.5">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[13px] font-medium">Emergency contact verification</span>
        <span className="text-xs text-surface-500">{verified} of {contacts.length} verified · 2 needed to activate</span>
      </div>
      {contacts.length === 0 && <p className="text-xs text-surface-500">The employee hasn't added emergency contacts yet.</p>}
      <ul className="divide-y divide-surface-100">
        {contacts.map(c => {
          const by = c.verifiedBy?.employee;
          return (
            <li key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
              <div className="min-w-0 flex-[1_1_220px]">
                <div className="text-[13px] font-medium">
                  {c.name} <span className="font-normal text-surface-500">· {c.relationship.replace(/_/g, " ").toLowerCase()}{c.isPrimary && " · primary"}</span>
                </div>
                <div className="text-xs text-surface-500">
                  {c.phone}
                  {c.verificationCallDate && <> · called {fmtDate(c.verificationCallDate)}{by && <> by {by.firstName} {by.lastName}</>}</>}
                </div>
                {c.verificationNotes && <div className="text-xs text-surface-600">“{c.verificationNotes}”</div>}
              </div>
              <ContactVerificationBadge status={c.verificationStatus} />
              <Button
                size="sm" variant="secondary" disabled={!canRecord}
                title={canRecord ? undefined : disabledReason}
                onClick={() => open(c)}
              >
                <PhoneCall size={12} /> {c.verificationStatus === "PENDING" ? "Record call" : "Update"}
              </Button>
            </li>
          );
        })}
      </ul>

      <Dialog open={!!target} onOpenChange={(o) => { if (!o) setTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record verification call</DialogTitle>
            <DialogDescription>{target?.name} · {target?.phone}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label htmlFor="ecv-outcome">Outcome</Label>
              <SelectField id="ecv-outcome" value={status} onChange={e => setStatus(e.target.value)}>
                <option value="VERIFIED">Verified — confirmed they know the employee</option>
                <option value="UNREACHABLE">Unreachable — no answer / switched off</option>
                <option value="FAILED">Failed — wrong number or did not confirm</option>
              </SelectField>
            </div>
            <div>
              <Label htmlFor="ecv-date">Call date</Label>
              <Input id="ecv-date" type="date" value={callDate} max={todayISO()} onChange={e => setCallDate(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="ecv-notes">Notes{status !== "VERIFIED" && " (required)"}</Label>
              <Textarea id="ecv-notes" rows={3} value={notes} onChange={e => setNotes(e.target.value)}
                placeholder={status === "VERIFIED" ? "Optional" : "What happened on the call?"} />
            </div>
            {error && <p className="text-xs text-[var(--color-error-fg)]">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setTarget(null)}>Cancel</Button>
            <AsyncButton onClick={() => save()} variant={status === "VERIFIED" ? "success" : "warning"} disabled={!callDate}>
              Save
            </AsyncButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
